import { useMemo } from 'react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type { AxiosRequestConfig } from 'axios';

import { fetcher, endpoints } from '@/lib/axios';
import type {
  IActualite,
  IActualiteDetail,
  IActualiteDetailResponse,
  IActualitesResponse,
  ITypeActualite,
  ITypeActualitesResponse,
} from '@/types/actualite';

// ----------------------------------------------------------------------

const swrOptions: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
};

// ----------------------------------------------------------------------

/**
 * Fetches all active news types (TypeActualites) for tab navigation.
 * Endpoint: GET /api/v1/type-actualites
 */
export function useGetTypeActualites() {
  const url = endpoints.typeActualites.list;

  const { data, isLoading, error, isValidating } = useSWR<ITypeActualitesResponse>(
    url,
    fetcher,
    swrOptions
  );

  return useMemo(
    () => ({
      typeActualites: (data?.data ?? []) as ITypeActualite[],
      typeActualitesLoading: isLoading,
      typeActualitesError: error as Error | undefined,
      typeActualitesValidating: isValidating,
      typeActualitesEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );
}

// ----------------------------------------------------------------------

/**
 * Fetches active actualités, optionally filtered by news type slug.
 * - Without typeSlug: GET /api/v1/actualites  (all types)
 * - With typeSlug:    GET /api/v1/actualites?type={typeSlug}
 *
 * Results are paginated (15 per page). SWR caches per unique URL+params key,
 * so switching tabs reuses previously fetched data without a network round-trip.
 */
export function useGetActualites(typeSlug?: string) {
  const url: string | [string, AxiosRequestConfig] = typeSlug
    ? [endpoints.actualites.list, { params: { type: typeSlug } }]
    : endpoints.actualites.list;

  const { data, isLoading, error, isValidating } = useSWR<IActualitesResponse>(
    url,
    fetcher,
    swrOptions
  );

  return useMemo(
    () => ({
      actualites: (data?.data ?? []) as IActualite[],
      actualitesLoading: isLoading,
      actualitesError: error as Error | undefined,
      actualitesValidating: isValidating,
      actualitesEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );
}

// ----------------------------------------------------------------------

/**
 * Fetches the full detail of a single actualité identified by its type slug
 * and article slug. Returns the article with all ordered sections.
 * Endpoint: GET /api/v1/actualites/{typeSlug}/{slug}
 */
export function useGetActualiteDetail(typeSlug: string, slug: string) {
  const url = typeSlug && slug ? endpoints.actualites.detail(typeSlug, slug) : null;

  const { data, isLoading, error, isValidating } = useSWR<IActualiteDetailResponse>(
    url,
    fetcher,
    swrOptions
  );

  return useMemo(
    () => ({
      actualite: (data?.data ?? null) as IActualiteDetail | null,
      actualiteLoading: isLoading,
      actualiteError: error as Error | undefined,
      actualiteValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );
}

// ----------------------------------------------------------------------

/**
 * Fetches the latest 3 active actualités, optionally for a formation category.
 * - With categorySlug: GET /api/v1/actualites/:categorySlug
 * - Without:           GET /api/v1/actualites
 *
 * @deprecated For the AllTheNews component use useGetActualites() instead.
 *             This hook is kept for backward compatibility with other consumers.
 */
export function useGetActualitesByCategory(categorySlug: string | undefined) {
  const url = categorySlug
    ? endpoints.actualites.byCategory(categorySlug)
    : endpoints.actualites.latest;

  const { data, isLoading, error, isValidating } = useSWR<IActualitesResponse>(
    url,
    fetcher,
    swrOptions
  );

  return useMemo(
    () => ({
      actualites: (data?.data ?? []) as IActualite[],
      actualitesLoading: isLoading,
      actualitesError: error as Error | undefined,
      actualitesValidating: isValidating,
      actualitesEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );
}
